import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControlLabel,
  FormGroup,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { useState } from "react";
import { APP_ICONS } from "../../assets";
import appcolors from "../../constants/Colors";
import IOSSwitch from "../IsoSwitch";
import { getKpiTiers } from "../../app/admin/campaign/AddEditCampaign";
import {
  calculateRemainingDays,
  getDeliveryFrequencyDuration,
  getPlanStatusStyle,
} from "../../utils";
import {
  ALL_BLOG_CATEGORY,
  CURRENCY,
  INVENTORY_MOVEMENT_REASON,
  PLAN_DELIVERY_TYPES,
  PROMOTIONAL_IMAGE_PAGE,
  PROMOTIONAL_IMAGE_SECTION,
} from "../../constants/Variables";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import CustomDropDown from "../CutomDropDown";
import GlobalButton from "../CustomButton";
import { useSelector } from "react-redux";
import StraightIcon from "@mui/icons-material/Straight";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const CREATOR_ONBOARDING_STATUS = ["pending", "approved", "rejected"];

const WITHDRAW_STATUS = [
  {
    value: "pending",
    label: {
      en: "Pending",
      ro: "În așteptare",
    },
  },
  {
    value: "approved",
    label: {
      en: "Approved",
      ro: "Aprobat",
    },
  },
  {
    value: "rejected",
    label: {
      en: "Rejected",
      ro: "Respins",
    },
  },
  {
    value: "paid",
    label: {
      en: "Paid",
      ro: "Plătit",
    },
  },
];

const tableStyle = {
  "&.MuiTableContainer-root .MuiTableHead-root": {
    backgroundColor: appcolors.primary,
  },
  "&.MuiTableContainer-root .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root":
    {
      padding: "6px 16px",
      color: appcolors.white,
      fontSize: "0.875rem",
    },
};

export default function DynamicPaginatedTable({
  tableWidth,
  minWidth,
  tableHeader,
  tableData,
  displayRows,
  showPagination = false,
  showDynamicPagination = false,
  handleChangeStatus,
  handleChangeFeatured,
  showActions,
  showEdit,
  showDelete,
  showLogin,
  handleEdit,
  handleDelete,
  handleLoginAs,
  handleViewDetail,
  handleChangeWithdrawStatus,
  handleChangeOnboardingStatus,
  handlePublishCampaign,
  handleCloseCampaign,
  handleViewParticipants,
  handleUploadReceipt,
  showDownloadBtn,
  handleDownloadInvoice,
  showViewDetail,
  paginatedData,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 20, 30, 50, 100],
  handleViewPricingDetails,
  isLoading = false,
}) {
  const { t } = useTranslation(["common"]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [meunAnchorEl, setMenuAnchorEl] = useState(null);
  const openAddEditMenu = Boolean(meunAnchorEl);
  const [rowData, setRowData] = useState(null);
  const { currentLanguage } = useSelector((state) => state.languageReducer);
  const visibleRows =
    (rowsPerPage && paginatedData === undefined) > 0
      ? tableData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : tableData;
  const skeletonRowCount = rowsPerPage || 10;

  const handleClickAddEditMenu = (event, row, index) => {
    setMenuAnchorEl(event.currentTarget);
    setRowData({ row, index });
  };

  const handleCloseAddEditMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getAppointmentWorkerStatusColor = (status) => {
    if (status === "rejected") return "red";
    if (status === "pending") return "orange";
    if (status === "accepted") return "blue";
  };

  const getAppointmentStatusColor = (status) => {
    if (status === "rejected") return "red";
    if (status === "pending") return "orange";
    if (status === "accepted") return "blue";
    if (status === "started") return "lighGreen";
    if (status === "completed") return "green";
  };

  const creatorOnboardingStatusColor = (status) => {
    if (status === "pending") return "orange";
    if (status === "approved") return "green";
    if (status === "rejected") return "red";
  };

  const campaignStatusColor = (status) => {
    if (status === "draft") return "grey";
    if (status === "published") return "green";
    if (status === "closed") return "red";
  };

  const withdrawStatusColor = (status) => {
    if (status === "pending") return "orange";
    if (status === "approved") return "blue";
    if (status === "paid") return "green";
    if (status === "rejected") return "red";
  };

  const getInventoryMovementReasonColor = (reason) => {
    if (reason === "stock_in") return "green";
    if (reason === "stock_out") return "red";
    if (reason === "release") return "blue";
    if (reason === "adjustment") return "orange";
    if (reason === "price_adjustment") return "purple";
    return "gray";
  };

  return (
    <>
      <TableContainer sx={tableStyle}>
        <Table
          sx={{
            width: tableWidth ? tableWidth : "100%",
            minWidth: minWidth ? minWidth : "900px",
          }}
        >
          <TableHead>
            <TableRow>
              {tableHeader?.map((header) => (
                <TableCell key={header}>{t(header)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
                <TableRow key={`skeleton-row-${rowIndex}`}>
                  {displayRows?.map((val, cellIndex) => (
                    <TableCell key={`${val}-${cellIndex}`}>
                      <Skeleton animation="wave" height={24} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : visibleRows?.length === 0 ? (
              <TableRow>
                <TableCell align="center" colSpan={tableHeader?.length || 1}>
                  <Typography fontWeight={500}>{t("noDataFound")}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibleRows?.map((row, index) => {
                return (
                  <TableRow key={index}>
                    {displayRows?.map((val) => (
                      <React.Fragment key={val}>
                        {val === "created_date" ? (
                          <TableCell>
                            {moment(row.createdAt).format("DD/MM/YYYY")}
                            <br />
                            {moment(row.createdAt).format("hh:mm:ss a")}
                          </TableCell>
                        ) : val === "user_name_supplier" ? (
                          <TableCell style={{ textTransform: "capitalize" }}>
                            {row?.username || "_"}
                          </TableCell>
                        ) : val === "status" ? (
                          <TableCell>
                            <FormGroup
                              sx={{
                                "&.MuiFormGroup-root .MuiFormControlLabel-root":
                                  {
                                    display: "flex",
                                    flexDirection: "column-reverse",
                                    justifyContent: "center",
                                    alignItems: "flex-start",
                                  },
                                "&.MuiFormGroup-root .MuiFormControlLabel-root .MuiFormControlLabel-label ":
                                  {
                                    fontSize: "0.75rem",
                                  },
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <IOSSwitch
                                    defaultChecked
                                    checked={row.status}
                                    onChange={(e) =>
                                      handleChangeStatus(
                                        e.target.checked,
                                        row,
                                        index,
                                      )
                                    }
                                  />
                                }
                                // label={row.status ? t("unBlock") : t("block")}
                              />
                            </FormGroup>
                          </TableCell>
                        ) : val === "featured" ? (
                          <TableCell>
                            <FormGroup
                              sx={{
                                "&.MuiFormGroup-root .MuiFormControlLabel-root":
                                  {
                                    display: "flex",
                                    flexDirection: "column-reverse",
                                    justifyContent: "center",
                                    alignItems: "flex-start",
                                  },
                                "&.MuiFormGroup-root .MuiFormControlLabel-root .MuiFormControlLabel-label ":
                                  {
                                    fontSize: "0.75rem",
                                  },
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <IOSSwitch
                                    defaultChecked
                                    checked={row.isFeatured}
                                    onChange={(e) =>
                                      handleChangeFeatured(
                                        e.target.checked,
                                        row,
                                        index,
                                      )
                                    }
                                  />
                                }
                                // label={row.status ? t("unBlock") : t("block")}
                              />
                            </FormGroup>
                          </TableCell>
                        ) : val === "supplier_details" ? (
                          <TableCell>
                            <Stack
                              gap={1.5}
                              direction={"row"}
                              alignItems={"center"}
                            >
                              <Avatar
                                src={row.profileImg}
                                sx={{
                                  height: "45px",
                                  width: "45px",
                                  bgcolor: "#b26bff",
                                }}
                              >
                                {!row.profileImg && row.businessName
                                  ? row.businessName.charAt(0).toUpperCase()
                                  : null}
                              </Avatar>
                              <Stack>
                                <Typography
                                  variant="body1"
                                  fontWeight={500}
                                  color={appcolors.black}
                                  lineHeight={"normal"}
                                >
                                  {row.businessName}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  color={"text.secondary"}
                                >
                                  {row.businessEmail}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "client_details" ? (
                          <TableCell>
                            <Stack
                              gap={1.5}
                              direction={"row"}
                              alignItems={"center"}
                            >
                              <Avatar
                                src={row.profileImg}
                                sx={{ height: "60px", width: "60px" }}
                              />
                              <Stack>
                                <Typography
                                  variant="body1"
                                  fontWeight={600}
                                  color={appcolors.black}
                                  textTransform={"capitalize"}
                                >
                                  {`${row.firstname} ${row.lastname}`}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={appcolors.gunmetal}
                                >
                                  {row.email}/{row.contact}
                                </Typography>
                                {row?.cui && (
                                  <Typography
                                    variant="body2"
                                    color={appcolors.gunmetal}
                                  >
                                    CUI:{row.cui}
                                  </Typography>
                                )}
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "client_sign_up_as" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {row.signupAs}
                          </TableCell>
                        ) : val === "creator_details" ? (
                          <TableCell>
                            <Stack
                              gap={1.5}
                              direction={"row"}
                              alignItems={"center"}
                            >
                              <Avatar
                                src={row.profileImg}
                                sx={{ height: "60px", width: "60px" }}
                              >
                                {!row.profileImg && row.username
                                  ? row.username.charAt(0).toUpperCase()
                                  : null}
                              </Avatar>
                              <Stack>
                                <Typography
                                  variant="body1"
                                  fontWeight={600}
                                  color={appcolors.black}
                                  textTransform={"capitalize"}
                                >
                                  {row.username}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={appcolors.gunmetal}
                                >
                                  {row.email}/{row.phoneNumber}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "creator_type" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {row.creatorType || "__"}
                          </TableCell>
                        ) : val === "creator_social_links" ? (
                          <TableCell>
                            <Stack direction={"row"} gap={0.5} flexWrap={"wrap"}>
                              {Object.entries(row.socialLinks || {})
                                .filter(([, link]) => link)
                                .map(([platform, link]) => (
                                  <Chip
                                    key={platform}
                                    label={platform}
                                    size="small"
                                    component="a"
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    clickable
                                    sx={{ textTransform: "capitalize" }}
                                  />
                                ))}
                              {!Object.values(row.socialLinks || {}).some(
                                Boolean,
                              ) && "__"}
                            </Stack>
                          </TableCell>
                        ) : val === "creator_onboarding_status" ? (
                          <TableCell sx={{ width: 200 }}>
                            <CustomDropDown
                              sx={{
                                color: creatorOnboardingStatusColor(row?.status),
                              }}
                              value={row?.status}
                              onChange={(e) =>
                                handleChangeOnboardingStatus(
                                  e.target.value,
                                  row,
                                )
                              }
                            >
                              {CREATOR_ONBOARDING_STATUS.map((status) => (
                                <MenuItem
                                  key={status}
                                  value={status}
                                  sx={{
                                    color: creatorOnboardingStatusColor(status),
                                  }}
                                >
                                  {t(status)}
                                </MenuItem>
                              ))}
                            </CustomDropDown>
                            {row?.status === "rejected" &&
                              row?.rejectionReason && (
                                <Typography
                                  variant="caption"
                                  color={"text.secondary"}
                                >
                                  {row.rejectionReason}
                                </Typography>
                              )}
                          </TableCell>
                        ) : val === "creator_account_status" ? (
                          <TableCell>
                            <IOSSwitch
                              checked={!!row.isActive}
                              onChange={(e) =>
                                handleChangeStatus(
                                  e.target.checked,
                                  row,
                                  index,
                                )
                              }
                            />
                          </TableCell>
                        ) : val === "campaign_details" ? (
                          <TableCell>
                            <Stack
                              gap={1.5}
                              direction={"row"}
                              alignItems={"center"}
                            >
                              <Avatar
                                variant="rounded"
                                src={row.coverImage}
                                sx={{ height: "60px", width: "60px" }}
                              >
                                {!row.coverImage && row.name
                                  ? row.name.charAt(0).toUpperCase()
                                  : null}
                              </Avatar>
                              <Stack>
                                <Typography
                                  variant="body1"
                                  fontWeight={600}
                                  color={appcolors.black}
                                >
                                  {row.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={appcolors.gunmetal}
                                >
                                  {t("starterBoxCount", {
                                    ns: "campaign",
                                    count: row.packages?.length || 0,
                                  })}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "campaign_type" ? (
                          <TableCell>{t(row.type)}</TableCell>
                        ) : val === "campaign_kpis" ? (
                          <TableCell>
                            <Stack gap={0.5}>
                              {(row.kpis || []).map((kpi) =>
                                getKpiTiers(kpi).map((tier, tierIndex) => (
                                  <Chip
                                    key={`${kpi.platform}-${tierIndex}`}
                                    size="small"
                                    sx={{ width: "fit-content" }}
                                    label={`${kpi.platform}: ${tier.minViews} - ${tier.maxViews} / ${tier.timeFrameDays}d / ${tier.budget ?? 0} RON`}
                                  />
                                ))
                              )}
                            </Stack>
                          </TableCell>
                        ) : val === "campaign_commission" ? (
                          <TableCell>{row.commission ?? 0}%</TableCell>
                        ) : val === "campaign_max_creators" ? (
                          <TableCell>{row.maxCreators ?? t("unlimited")}</TableCell>
                        ) : val === "campaign_status" ? (
                          <TableCell>
                            <Chip
                              size="small"
                              label={t(row.status)}
                              sx={{
                                color: "#fff",
                                backgroundColor: campaignStatusColor(row.status),
                              }}
                            />
                          </TableCell>
                        ) : val === "campaign_actions" ? (
                          <TableCell>
                            <Stack
                              gap={1}
                              direction={"row"}
                              alignItems={"center"}
                            >
                              {row.status === "draft" && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={() => handlePublishCampaign(row)}
                                >
                                  {t("publish")}
                                </Button>
                              )}
                              {row.status === "published" && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="error"
                                  onClick={() => handleCloseCampaign(row)}
                                >
                                  {t("closeCampaign")}
                                </Button>
                              )}
                              {row.status !== "draft" && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleViewParticipants(row)}
                                >
                                  {t("requests", { ns: "campaign" })} (
                                  {row.pendingParticipants || 0}/
                                  {row.acceptedParticipants || 0})
                                </Button>
                              )}
                              {row.submissionsToReview > 0 && (
                                <Chip
                                  size="small"
                                  color="warning"
                                  clickable
                                  onClick={() =>
                                    handleViewParticipants(row, "accepted")
                                  }
                                  label={t("videosToReview", {
                                    ns: "campaign",
                                    count: row.submissionsToReview,
                                  })}
                                />
                              )}
                              {row.status !== "closed" && (
                                <img
                                  src={APP_ICONS.editMustardIcon}
                                  alt="edit-icon"
                                  width={20}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleEdit({ row, index })}
                                />
                              )}
                              {row.status === "draft" && (
                                <img
                                  src={APP_ICONS.deleteRedIcon}
                                  alt="delete-icon"
                                  width={20}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleDelete({ row, index })}
                                />
                              )}
                            </Stack>
                          </TableCell>
                        ) : val === "company_details" ? (
                          <TableCell>
                            <Stack
                              gap={1.5}
                              direction={"row"}
                              alignItems={"center"}
                            >
                              <Avatar
                                src={row.profileImg}
                                sx={{
                                  height: "45px",
                                  width: "45px",
                                  bgcolor: "#b26bff",
                                }}
                                alt={row.name || "User"}
                              >
                                {!row.profileImg && row.name
                                  ? row.name.charAt(0).toUpperCase()
                                  : null}
                              </Avatar>
                              <Stack>
                                <Typography
                                  variant="body1"
                                  fontWeight={500}
                                  color={appcolors.black}
                                  lineHeight={"normal"}
                                >
                                  {row.name}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  color={"text.secondary"}
                                >
                                  {row.email}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "worker_details" ? (
                          <TableCell>
                            <Stack
                              gap={1.5}
                              direction={"row"}
                              alignItems={"center"}
                            >
                              <Avatar
                                src={row.profileImg}
                                sx={{ height: "60px", width: "60px" }}
                              />
                              <Stack>
                                <Typography
                                  variant="body1"
                                  fontWeight={600}
                                  color={appcolors.black}
                                >
                                  {row.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={appcolors.gunmetal}
                                >
                                  {row.email}/{row.contact}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  color={appcolors.gunmetal}
                                  textTransform={"capitalize"}
                                >
                                  {row.location?.addressLabel || ""}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "company_worker_detail" ? (
                          <TableCell>
                            <Stack
                              gap={1.5}
                              direction={"row"}
                              alignItems={"center"}
                            >
                              <Avatar
                                src={row.companyId.profileImg}
                                sx={{ height: "60px", width: "60px" }}
                              />
                              <Stack>
                                <Typography
                                  variant="body1"
                                  fontWeight={600}
                                  color={appcolors.black}
                                >
                                  {row.companyId.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={appcolors.gunmetal}
                                >
                                  {row.companyId.email}/{row.companyId.contact}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  color={appcolors.gunmetal}
                                  textTransform={"capitalize"}
                                >
                                  {row.companyId.location?.addressLabel}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "package_duration" ? (
                          <TableCell>
                            {`${moment(row.startDate).format("MM/DD/YYYY")} ${t(
                              "to",
                            )} ${moment(row.endData).format("MM/DD/YYYY")}`}
                          </TableCell>
                        ) : val === "package_left_days" ? (
                          <TableCell>
                            {row.status === "expired"
                              ? `0 ${t("days")}`
                              : calculateRemainingDays(row.endDate) +
                                " " +
                                t("days")}
                          </TableCell>
                        ) : val === "active_package_name" ? (
                          <TableCell
                            style={{ textTransform: "capitalize" }}
                          >{`${row?.subscriptionId?.name[currentLanguage]}`}</TableCell>
                        ) : val === "subscription_status" ? (
                          <TableCell>
                            <Chip
                              sx={{
                                width: "60px",
                                borderRadius: "6px",
                                backgroundColor:
                                  row.status === "active"
                                    ? "#00c100"
                                    : "#ff0000e8",
                                "& .MuiChip-label": {
                                  padding: 0,
                                  color: "#ffffff",
                                },
                              }}
                              label={
                                row.status === "active"
                                  ? t("active")
                                  : t("expired")
                              }
                            />
                          </TableCell>
                        ) : val === "appointment_gen_id" ? (
                          <TableCell>#{row?.appointmentId}</TableCell>
                        ) : val === "appointment_service" ? (
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={0.4}
                            >
                              <Avatar
                                src={row.serviceSubCategoryDetails.image}
                                sx={{
                                  height: 50,
                                  width: 50,
                                  borderRadius: 1,
                                  border: "1px solid grey",
                                }}
                              />
                              <Box>
                                <Typography textTransform={"capitalize"}>
                                  {
                                    row.serviceSubCategoryDetails.name[
                                      currentLanguage
                                    ]
                                  }
                                </Typography>
                                <Typography
                                  variant="body2"
                                  textTransform={"capitalize"}
                                  maxWidth={150}
                                  noWrap
                                  color={"custom.slateGrey"}
                                >
                                  {
                                    row.serviceSubCategoryDetails.description[
                                      currentLanguage
                                    ]
                                  }
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                        ) : val === "appointment_worker" ? (
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={0.4}
                            >
                              <Avatar
                                src={row.workerDetails.profileImg}
                                sx={{
                                  height: 50,
                                  width: 50,
                                  borderRadius: 1,
                                  border: "1px solid grey",
                                }}
                              />
                              <Box>
                                <Typography textTransform={"capitalize"}>
                                  {row.workerDetails.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  textTransform={"capitalize"}
                                  maxWidth={150}
                                  noWrap
                                  color={"custom.slateGrey"}
                                >
                                  {row.workerDetails.bio}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                        ) : val === "appointment_company" ? (
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={0.4}
                            >
                              <Avatar
                                src={row.companyDetails.profileImg}
                                sx={{
                                  height: 50,
                                  width: 50,
                                  borderRadius: 1,
                                  border: "1px solid grey",
                                }}
                              />
                              <Box>
                                <Typography textTransform={"capitalize"}>
                                  {row.companyDetails.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  textTransform={"capitalize"}
                                  maxWidth={150}
                                  noWrap
                                  color={"custom.slateGrey"}
                                >
                                  {row.companyDetails.expertise}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                        ) : val === "appointment_worker_status" ? (
                          <TableCell>
                            <Chip
                              label={row.workerStatus}
                              sx={{
                                textTransform: "capitalize",
                                color: "custom.white",
                                backgroundColor:
                                  getAppointmentWorkerStatusColor(
                                    row.workerStatus,
                                  ),
                              }}
                            />
                          </TableCell>
                        ) : val === "appointment_status" ? (
                          <TableCell>
                            <Chip
                              label={row.status}
                              sx={{
                                textTransform: "capitalize",
                                color: "custom.white",
                                backgroundColor: getAppointmentStatusColor(
                                  row.status,
                                ),
                              }}
                            />
                          </TableCell>
                        ) : val === "appointment_cost" ? (
                          <TableCell>
                            {row.totalCost} {CURRENCY}
                          </TableCell>
                        ) : val === "detail_chip" ? (
                          <TableCell>
                            <Chip
                              icon={<RemoveRedEyeIcon />}
                              label={t("viewDetail")}
                              onClick={() => handleViewDetail(row, index)}
                            />
                          </TableCell>
                        ) : val === "withdraw_user_detail" ? (
                          <TableCell>
                            <Stack
                              gap={1.5}
                              direction={"row"}
                              alignItems={"center"}
                            >
                              <Avatar
                                src={row.user?.profileImg}
                                sx={{ height: "40px", width: "40px" }}
                              />
                              <Stack>
                                <Typography variant="body1" fontWeight={600}>
                                  {`${row.user?.firstname} ${row.user?.lastname}`}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={appcolors.gunmetal}
                                >
                                  {row.user?.email}/{row.user?.contact}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "withdraw_bank_detail" ? (
                          <TableCell>
                            <Stack>
                              <Typography variant="body2">
                                {t("accountHolder")}:{" "}
                                {row.user?.bankDetails?.accountHolderName}
                              </Typography>
                              <Typography variant="body2">
                                {t("bankName")}:{" "}
                                {row.user?.bankDetails?.bankName}
                              </Typography>
                              <Typography variant="body2">
                                {t("iban")}: {row.user?.bankDetails?.iban}
                              </Typography>
                            </Stack>
                          </TableCell>
                        ) : val === "withdraw_amount" ? (
                          <TableCell>
                            {row.amount} {CURRENCY}
                          </TableCell>
                        ) : val === "withdraw_amount" ? (
                          <TableCell>
                            {row.amount} {CURRENCY}
                          </TableCell>
                        ) : val === "withdraw_status" ? (
                          <TableCell sx={{ width: 200 }}>
                            {["rejected", "paid"].includes(row?.status) ? (
                              <Typography
                                color={withdrawStatusColor(row?.status)}
                                fontWeight={500}
                                textTransform={"capitalize"}
                              >
                                {
                                  WITHDRAW_STATUS.find(
                                    (val) => val.value === row?.status,
                                  )?.label[currentLanguage]
                                }
                              </Typography>
                            ) : (
                              <CustomDropDown
                                sx={{
                                  color: withdrawStatusColor(row?.status),
                                }}
                                value={row?.status}
                                onChange={(e) =>
                                  handleChangeWithdrawStatus(
                                    e.target.value,
                                    row,
                                  )
                                }
                              >
                                {WITHDRAW_STATUS.map((val, index) => {
                                  return (
                                    <MenuItem
                                      key={index}
                                      value={val.value}
                                      sx={{
                                        color: withdrawStatusColor(val.value),
                                      }}
                                    >
                                      {val.label[currentLanguage]}
                                    </MenuItem>
                                  );
                                })}
                              </CustomDropDown>
                            )}
                          </TableCell>
                        ) : val === "withdraw_reason" ? (
                          <TableCell>
                            {row.status === "rejected" ? (
                              <Typography variant="body2">
                                {row.reason || "__"}
                              </Typography>
                            ) : (
                              <Typography variant="body2">__</Typography>
                            )}
                          </TableCell>
                        ) : val === "withdraw_upload_receipt" ? (
                          <TableCell>
                            {row?.receipt && row?.status === "paid" ? (
                              <GlobalButton
                                btnText={"view"}
                                handleClickBtn={() =>
                                  window.open(row?.receipt, "_blank")
                                }
                                disabled={row?.status === "rejected"}
                                styleProps={{
                                  borderRadius: 1,
                                  height: "30px",
                                  width: "auto",
                                  fontSize: "0.875rem",
                                }}
                              />
                            ) : row?.status === "paid" ? (
                              <GlobalButton
                                btnText={"uploadReceipt"}
                                disabled={row?.status === "rejected"}
                                handleClickBtn={() =>
                                  handleUploadReceipt(row, index)
                                }
                                styleProps={{
                                  borderRadius: 1,
                                  height: "30px",
                                  width: "auto",
                                  fontSize: "0.875rem",
                                }}
                              />
                            ) : (
                              "__"
                            )}
                          </TableCell>
                        ) : val === "faq_question_both" ? (
                          <TableCell>
                            <Typography>
                              {" "}
                              {row?.question[currentLanguage]}
                            </Typography>
                          </TableCell>
                        ) : val === "faq_answer_both" ? (
                          <TableCell>
                            <Typography>
                              {row?.answer[currentLanguage]}
                            </Typography>
                          </TableCell>
                        ) : val === "blog_title" ? (
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={1}
                            >
                              <Avatar
                                src={row?.image}
                                sx={{
                                  height: "40px",
                                  width: "40px",
                                  borderRadius: 1,
                                }}
                              />
                              <Tooltip title={row?.title[currentLanguage]}>
                                <Typography
                                  sx={{
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    display: "-webkit-box",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: "400px",
                                  }}
                                >
                                  {row?.title[currentLanguage]}
                                </Typography>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        ) : val === "blog_category" ? (
                          <TableCell>
                            <Typography sx={{ whiteSpace: "nowrap" }}>
                              {
                                ALL_BLOG_CATEGORY.find(
                                  (val) => val.type === row?.category,
                                )?.title[currentLanguage]
                              }
                            </Typography>
                          </TableCell>
                        ) : val === "blog_topic" ? (
                          <TableCell>
                            <Typography
                              sx={{
                                whiteSpace: "nowrap",
                                textTransform: "capitalize",
                              }}
                            >
                              {row?.topic?.title[currentLanguage] ||
                                t("notAddedYet")}
                            </Typography>
                          </TableCell>
                        ) : val === "blog_small_description" ? (
                          <TableCell>
                            <Tooltip
                              title={row?.smallDescription[currentLanguage]}
                            >
                              <Typography
                                sx={{
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  display: "-webkit-box",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "700px",
                                }}
                              >
                                {row?.smallDescription[currentLanguage]}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        ) : val === "event_category_name" ? (
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={1}
                              width={"400px"}
                            >
                              <Avatar
                                src={row?.image}
                                sx={{
                                  height: "40px",
                                  width: "40px",
                                  backgroundColor: "#edebeb",
                                  border: "1px solid #edebeb",
                                  borderRadius: "6px",
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  backgroundColor: "primary.main",
                                }}
                              >
                                {!row?.image && row?.name?.[currentLanguage]
                                  ? row.name[currentLanguage]
                                      .slice(0, 2)
                                      .toUpperCase()
                                  : null}
                              </Avatar>
                              <Typography sx={{ textTransform: "capitalize" }}>
                                {row?.name[currentLanguage] || "__"}
                              </Typography>
                            </Stack>
                          </TableCell>
                        ) : val === "event_category_description" ? (
                          <TableCell>
                            <Tooltip title={row?.description[currentLanguage]}>
                              <Typography
                                sx={{
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  display: "-webkit-box",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "700px",
                                }}
                              >
                                {row?.description[currentLanguage] || "__"}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        ) : val === "plan_name" ? (
                          <TableCell>
                            <Stack>
                              <Typography variant="body1">
                                {row?.name || "__"}
                              </Typography>
                              <Typography
                                variant="body2"
                                color={"text.secondary"}
                              >{`${row?.totalItems} ${t("items")}`}</Typography>
                            </Stack>
                          </TableCell>
                        ) : val === "plan_created_by" ? (
                          <TableCell>
                            <Stack>
                              <Typography variant="body1">
                                {row?.user?.firstname} {row?.user?.lastname}
                              </Typography>
                              <Typography
                                variant="body2"
                                color={"text.secondary"}
                              >
                                {row?.user?.email}
                              </Typography>
                              <Typography
                                variant="body2"
                                color={"text.secondary"}
                              >
                                {row?.user?.contact}
                              </Typography>
                            </Stack>
                          </TableCell>
                        ) : val === "delivery_frequency" ? (
                          <TableCell>
                            <Box>
                              <Typography variant="body1" fontWeight={500}>
                                {PLAN_DELIVERY_TYPES.find(
                                  (x) => x.value === row.deliveryFrequency,
                                )?.label[currentLanguage] || "__"}
                              </Typography>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t(
                                  getDeliveryFrequencyDuration(
                                    row.deliveryFrequency,
                                  ),
                                )}
                              </Typography>
                            </Box>
                          </TableCell>
                        ) : val === "next_delivery_date" ? (
                          <TableCell>
                            {row.nextDeliveryAt ? (
                              <Box>
                                <Typography variant="body1" fontWeight={500}>
                                  {moment(row.nextDeliveryAt).format(
                                    "DD MMM YYYY",
                                  )}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {(() => {
                                    const diffDays = moment(row.nextDeliveryAt)
                                      .startOf("day")
                                      .diff(moment().startOf("day"), "days");

                                    if (diffDays > 0) {
                                      return `${t("in")} ${diffDays} ${t("days")}`;
                                    }

                                    if (diffDays === 0) {
                                      return t("today");
                                    }

                                    return t("overdue");
                                  })()}
                                </Typography>
                              </Box>
                            ) : (
                              <Box>
                                <Typography variant="body1" fontWeight={500}>
                                  __
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {t("notSheduled")}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                        ) : val === "validity_period" ? (
                          <TableCell>
                            {row.validityPeriod ? (
                              <Box>
                                <Typography variant="body1" fontWeight={500}>
                                  {moment(row.validityPeriod).format(
                                    "DD MMM YYYY",
                                  )}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {(() => {
                                    const diffDays = moment(row.validityPeriod)
                                      .startOf("day")
                                      .diff(moment().startOf("day"), "days");

                                    if (diffDays > 0) {
                                      return `${t("in")} ${diffDays} ${t("days")}`;
                                    }

                                    if (diffDays === 0) {
                                      return t("today");
                                    }

                                    return t("expired");
                                  })()}
                                </Typography>
                              </Box>
                            ) : (
                              <Box>
                                <Typography variant="body1" fontWeight={500}>
                                  __
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {t("notSheduled")}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                        ) : val === "plan_status" ? (
                          <TableCell>
                            <Box
                              display="inline-flex"
                              alignItems="center"
                              gap={1}
                              px={1.25}
                              py={0.55}
                              borderRadius="10px"
                              border="1px solid"
                              borderColor={getPlanStatusStyle(row.status)}
                              bgcolor={getPlanStatusStyle(row.status).bgColor}
                              color={getPlanStatusStyle(row.status).color}
                              fontWeight={500}
                              fontSize="0.95rem"
                              textTransform="capitalize"
                            >
                              <Box
                                width={8}
                                height={8}
                                borderRadius="50%"
                                bgcolor={
                                  getPlanStatusStyle(row.status).dotColor
                                }
                              />
                              {row.status}
                            </Box>
                          </TableCell>
                        ) : val === "download" ? (
                          <TableCell>
                            {showDownloadBtn && (
                              <Tooltip title={t("downloadInvoice")}>
                                <Stack
                                  justifyContent={"center"}
                                  alignItems={"center"}
                                  onClick={() =>
                                    handleDownloadInvoice(row, index)
                                  }
                                  sx={{ cursor: "pointer" }}
                                >
                                  <StraightIcon
                                    htmlColor={appcolors.primary}
                                    sx={{ transform: "rotate(180deg)" }}
                                  />
                                  <Typography
                                    variant="body2"
                                    fontSize={"0.75rem"}
                                    color={"#343A40"}
                                  >
                                    {t("download")}
                                  </Typography>
                                </Stack>
                              </Tooltip>
                            )}
                          </TableCell>
                        ) : val === "inventory_store" ? (
                          <TableCell>
                            <Typography variant="body2">
                              {row?.supplierData?.businessName || "--"}
                            </Typography>
                          </TableCell>
                        ) : val === "inventory_product_detail" ? (
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={1}
                              width={"400px"}
                            >
                              <Avatar
                                src={row?.productData?.coverImage}
                                sx={{
                                  height: "40px",
                                  width: "40px",
                                  backgroundColor: "#edebeb",
                                  border: "1px solid #edebeb",
                                  borderRadius: "6px",
                                }}
                              />
                              <Stack>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    display: "-webkit-box",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: "400px",
                                  }}
                                >
                                  {row?.productData?.name[currentLanguage]}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={"text.secondary"}
                                >
                                  {t("sku")}: {row?.sku}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "inventory_movement_reason" ? (
                          <TableCell>
                            <Chip
                              label={
                                INVENTORY_MOVEMENT_REASON.find(
                                  (val) => val.value === row?.reason,
                                )?.label[currentLanguage]
                              }
                              variant="filled"
                              size="small"
                              sx={{
                                textTransform: "capitalize",
                                color: "custom.white",
                                backgroundColor:
                                  getInventoryMovementReasonColor(row?.reason),
                              }}
                            />
                          </TableCell>
                        ) : val === "inventory_quantity_change" ? (
                          <TableCell>
                            <Typography variant="body2">
                              {row?.quantityChange}
                            </Typography>
                          </TableCell>
                        ) : val === "inventory_previous_quantity" ? (
                          <TableCell>
                            <Typography variant="body2">
                              {row?.previousQuantity}
                            </Typography>
                          </TableCell>
                        ) : val === "inventory_current_quantity" ? (
                          <TableCell>
                            <Typography variant="body2">
                              {row?.currentQuantity}
                            </Typography>
                          </TableCell>
                        ) : val === "inventory_pricing_details" ? (
                          <TableCell>
                            <Chip
                              variant="filled"
                              label={t("view")}
                              clickable
                              onClick={() =>
                                handleViewPricingDetails(row, index)
                              }
                              size="small"
                              sx={{
                                textTransform: "capitalize",
                                color: "custom.white",
                                backgroundColor: "primary.main",
                                borderRadius: "6px",
                                "&:hover": {
                                  backgroundColor: "primary.dark",
                                },
                              }}
                            />
                          </TableCell>
                        ) : val === "promotional_block_title" ? (
                          <TableCell>
                            <Typography>
                              {" "}
                              {row?.title[currentLanguage]}
                            </Typography>
                          </TableCell>
                        ) : val === "promotional_block_page" ? (
                          <TableCell>
                            <Typography>
                              {" "}
                              {
                                PROMOTIONAL_IMAGE_PAGE.find(
                                  (val) => val.value === row?.page,
                                )?.label[currentLanguage]
                              }
                            </Typography>
                          </TableCell>
                        ) : val === "promotional_block_position_type" ? (
                          <TableCell>
                            <Typography>
                              {" "}
                              {
                                PROMOTIONAL_IMAGE_SECTION.find(
                                  (val) => val.value === row?.positionType,
                                )?.label[currentLanguage]
                              }
                            </Typography>
                          </TableCell>
                        ) : val === "promotional_block_start_date" ? (
                          <TableCell>
                            <Typography>
                              {" "}
                              {moment(row?.startDate).format("DD/MM/YYYY")}
                            </Typography>
                          </TableCell>
                        ) : val === "promotional_block_end_date" ? (
                          <TableCell>
                            <Typography>
                              {" "}
                              {moment(row?.endDate).format("DD/MM/YYYY")}
                            </Typography>
                          </TableCell>
                        ) : val === "charity_organization_name" ? (
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={1}
                            >
                              <Avatar
                                src={row?.logo || null}
                                sx={{
                                  height: "45px",
                                  width: "45px",
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: "6px",
                                  "& .MuiAvatar-img": {
                                    objectFit: "contain",
                                  },
                                }}
                              >
                                {row?.name?.[currentLanguage]
                                  ? row?.name?.[currentLanguage]
                                      .charAt(0)
                                      .toUpperCase()
                                  : null}
                              </Avatar>
                              <Tooltip title={row?.name[currentLanguage]}>
                                <Typography
                                  sx={{
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    display: "-webkit-box",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: "400px",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {" "}
                                  {row?.name[currentLanguage]}
                                </Typography>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        ) : val === "charity_organization_description" ? (
                          <TableCell>
                            <Tooltip title={row?.description[currentLanguage]}>
                              <Typography
                                sx={{
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  display: "-webkit-box",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "400px",
                                }}
                              >
                                {" "}
                                {row?.description[currentLanguage]}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        ) : val === "charity_organization_website" ? (
                          <TableCell>
                            {row?.website ? (
                              <Chip
                                variant="filled"
                                label={t("visit")}
                                clickable
                                component="a"
                                href={row.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                sx={{
                                  textTransform: "capitalize",
                                  color: "custom.white",
                                  backgroundColor: "primary.main",
                                  borderRadius: "6px",
                                  "&:hover": {
                                    backgroundColor: "primary.dark",
                                  },
                                }}
                              />
                            ) : (
                              "__"
                            )}
                          </TableCell>
                        ) : val === "donation_order_id" ? (
                          <TableCell>{row?.order?.orderId}</TableCell>
                        ) : val === "donation_organization_name" ? (
                          <TableCell>
                            <Tooltip
                              title={row?.organization?.name[currentLanguage]}
                            >
                              <Typography
                                sx={{
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  display: "-webkit-box",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "400px",
                                  textTransform: "capitalize",
                                }}
                              >
                                {" "}
                                {row?.organization?.name[currentLanguage]}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        ) : val === "donation_user" ? (
                          <TableCell>
                            <Stack
                              gap={1.5}
                              direction={"row"}
                              alignItems={"center"}
                            >
                              <Avatar
                                src={row?.user?.profileImg}
                                sx={{ height: "60px", width: "60px" }}
                              />
                              <Stack>
                                <Typography
                                  variant="body1"
                                  fontWeight={600}
                                  color={appcolors.black}
                                  textTransform={"capitalize"}
                                >
                                  {`${row?.user?.firstname} ${row?.user?.lastname}`}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={appcolors.gunmetal}
                                >
                                  {row?.user?.email}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "donation_sub_total" ? (
                          <TableCell>
                            {row?.orderSubtotal} {CURRENCY}
                          </TableCell>
                        ) : val === "donation_donation_percentage" ? (
                          <TableCell>{row?.donationPercentage} %</TableCell>
                        ) : val === "donation_donation_expected_amount" ? (
                          <TableCell>
                            {row?.donationAmount} {CURRENCY}
                          </TableCell>
                        ) : val === "donation_donation_actual_amount" ? (
                          <TableCell>
                            {row?.actualAmount} {CURRENCY}
                          </TableCell>
                        ) : val === "brand_name" ||
                          val === "blog_topic_name" ? (
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={1}
                            >
                              <Avatar
                                src={row?.logo || null}
                                sx={{
                                  height: "45px",
                                  width: "45px",
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: "6px",
                                  "& .MuiAvatar-img": {
                                    objectFit: "contain",
                                  },
                                }}
                              >
                                {row?.title?.[currentLanguage]
                                  ? row?.title?.[currentLanguage]
                                      .charAt(0)
                                      .toUpperCase()
                                  : null}
                              </Avatar>
                              <Tooltip title={row?.title[currentLanguage]}>
                                <Typography
                                  sx={{
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    display: "-webkit-box",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: "400px",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {" "}
                                  {row?.title[currentLanguage]}
                                </Typography>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        ) : val === "brand_description" ||
                          val === "blog_topic_description" ? (
                          <TableCell>
                            <Tooltip title={row?.description[currentLanguage]}>
                              <Typography
                                sx={{
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  display: "-webkit-box",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "400px",
                                  textTransform: "capitalize",
                                }}
                              >
                                {" "}
                                {row?.description[currentLanguage]}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        ) : val === "gift_card_name" ? (
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={1}
                            >
                              <Avatar
                                src={row?.image || null}
                                sx={{
                                  height: "45px",
                                  width: "45px",
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: "6px",
                                  "& .MuiAvatar-img": {
                                    objectFit: "contain",
                                  },
                                }}
                              >
                                {row?.title?.[currentLanguage]
                                  ? row?.title?.[currentLanguage]
                                      .charAt(0)
                                      .toUpperCase()
                                  : null}
                              </Avatar>
                              <Tooltip title={row?.title[currentLanguage]}>
                                <Typography
                                  sx={{
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    display: "-webkit-box",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: "400px",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {" "}
                                  {row?.title[currentLanguage]}
                                </Typography>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        ) : val === "gift_card_description" ? (
                          <TableCell>
                            <Tooltip title={row?.description[currentLanguage]}>
                              <Typography
                                sx={{
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  display: "-webkit-box",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "400px",
                                  textTransform: "capitalize",
                                }}
                              >
                                {" "}
                                {row?.description[currentLanguage]}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        ) : val === "gift_card_amount" ? (
                          <TableCell>{`${row?.amount} ${CURRENCY}`}</TableCell>
                        ) : val === "promo_code" ? (
                          <TableCell>{row?.code}</TableCell>
                        ) : val === "promo_code_description" ? (
                          <TableCell>
                            <Tooltip title={row?.description[currentLanguage]}>
                              <Typography
                                sx={{
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  display: "-webkit-box",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "400px",
                                  textTransform: "capitalize",
                                }}
                              >
                                {" "}
                                {row?.description[currentLanguage]}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        ) : val === "promo_code_value" ? (
                          <TableCell>{`${row?.discountValue} %`}</TableCell>
                        ) : val === "promo_code_max_discount" ? (
                          <TableCell>{`${row?.maxDiscountAmount} ${CURRENCY}`}</TableCell>
                        ) : val === "promo_code_min_order" ? (
                          <TableCell>{`${row?.minOrderAmount} ${CURRENCY}`}</TableCell>
                        ) : val === "promo_code_usage_limit" ? (
                          <TableCell>{row?.usageLimit}</TableCell>
                        ) : val === "promo_code_used_count" ? (
                          <TableCell>{row?.usedCount}</TableCell>
                        ) : val === "promo_code_duration" ? (
                          <TableCell>
                            {`${moment(row.startDate).format("MM/DD/YYYY")} ${t(
                              "to",
                            )} ${moment(row.endData).format("MM/DD/YYYY")}`}
                          </TableCell>
                        ) : val === "actions" ? (
                          <TableCell>
                            <Stack
                              gap={1}
                              direction={"row"}
                              alignItems={"center"}
                            >
                              {/* {showViewDetail && (
                                // <IconButton
                                //   onClick={() => {
                                //     handleViewDetail(row, index);
                                //   }}
                                // >
                                <RemoveRedEyeIcon
                                  htmlColor={appcolors.primary}
                                  size="small"
                                />
                                // </IconButton>
                              )} */}
                              {showActions && (
                                <>
                                  <Button
                                    disableRipple
                                    id="addEdit-menu-button"
                                    aria-controls={
                                      openAddEditMenu
                                        ? "addEdit-menu"
                                        : undefined
                                    }
                                    startIcon={
                                      <MoreVertIcon htmlColor="#000000" />
                                    }
                                    aria-haspopup="true"
                                    aria-expanded={
                                      openAddEditMenu ? "true" : undefined
                                    }
                                    sx={{
                                      width: "auto",
                                      padding: "5px",
                                      minWidth: 0,
                                      "& .MuiButton-startIcon": {
                                        mx: 0,
                                      },
                                      "&:hover": {
                                        backgroundColor: "#ffffff",
                                      },
                                    }}
                                    onClick={(e) =>
                                      handleClickAddEditMenu(e, row, index)
                                    }
                                  ></Button>
                                  <Menu
                                    id="addEdit-menu"
                                    anchorEl={meunAnchorEl}
                                    open={openAddEditMenu}
                                    onClose={handleCloseAddEditMenu}
                                    MenuListProps={{
                                      "aria-labelledby": "addEdit-menu-button",
                                    }}
                                    sx={{
                                      ".MuiMenu-paper": {
                                        boxShadow: "none",
                                        border: "1px solid grey",
                                      },
                                    }}
                                  >
                                    <Stack
                                      direction={"column"}
                                      alignItems={"center"}
                                      spacing={1}
                                      paddingX={1}
                                    >
                                      {showEdit && (
                                        <img
                                          src={APP_ICONS.editMustardIcon}
                                          alt="delete-icon"
                                          width={20}
                                          style={{
                                            cursor: "pointer",
                                          }}
                                          onClick={() => {
                                            handleEdit(rowData);
                                            handleCloseAddEditMenu();
                                          }}
                                        />
                                      )}
                                      {showDelete && (
                                        <img
                                          src={APP_ICONS.deleteRedIcon}
                                          alt="delete-icon"
                                          width={20}
                                          style={{
                                            cursor: "pointer",
                                          }}
                                          onClick={() => {
                                            handleDelete(rowData);
                                            handleCloseAddEditMenu();
                                          }}
                                        />
                                      )}
                                      {showViewDetail && (
                                        // <IconButton
                                        //   onClick={() => {
                                        //     handleViewDetail(rowData);
                                        //     handleCloseAddEditMenu();
                                        //   }}
                                        // >
                                        <RemoveRedEyeIcon
                                          onClick={() =>
                                            handleViewDetail(rowData, index)
                                          }
                                          sx={{
                                            cursor: "pointer",
                                            color: appcolors.primary,
                                          }}
                                        />
                                        // </IconButton>
                                      )}

                                      {showLogin && (
                                        <img
                                          src={APP_ICONS.logoutPurpleIcon}
                                          alt="login-icon"
                                          width={20}
                                          style={{
                                            cursor: "pointer",
                                          }}
                                          onClick={() => {
                                            handleLoginAs(rowData);
                                            handleCloseAddEditMenu();
                                          }}
                                        />
                                      )}
                                    </Stack>
                                  </Menu>
                                </>
                              )}
                            </Stack>
                          </TableCell>
                        ) : (
                          <TableCell>
                            <Typography lineHeight="normal" fontSize={"1rem"}>
                              {row[val]}
                            </Typography>
                          </TableCell>
                        )}
                      </React.Fragment>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {showPagination && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={tableData?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t("rowsPerPage")}
        />
      )}
      {showDynamicPagination && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={paginatedData.total}
          rowsPerPage={paginatedData.limit}
          page={paginatedData.page - 1}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          labelRowsPerPage={t("rowsPerPage")}
          disabled={isLoading}
        />
      )}
    </>
  );
}
